import { __rest, __spreadArrays, __assign } from '../../../../node_modules/tslib/tslib.es6.js';
import React, { useState, useRef, useContext, useEffect } from 'react';
import { Icon } from '../../icon/index.js';
import { FCTheme } from '../../../theming/FCTheme.js';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import isoWeek from '../../../../node_modules/dayjs/plugin/isoWeek.js';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import duration from 'dayjs/plugin/duration';
import { DayName, Week, Day, CalendarWrapper, CalendarHeader, CalendarTitle, CalendarControl, CalendarControlButtons, SvgWrapper, Table } from './style.js';
dayjs.extend(localeData);
dayjs.extend(advancedFormat);
dayjs.extend(isoWeek);
dayjs.extend(duration);

var Calendar = function Calendar(_a) {
  var _b = _a.onDateChange,
      onDateChange = _b === void 0 ? function () {
    return undefined;
  } : _b,
      selectedDate = _a.selectedDate,
      autoFocusDay = _a.autoFocusDay,
      size = _a.size,
      inputRef = _a.inputRef,
      menuRef = _a.menuRef,
      setMenuOpen = _a.setMenuOpen,
      _c = _a.dateFormat,
      dateFormat = _c === void 0 ? 'MM/DD/YYYY' : _c,
      rest = __rest(_a, ["onDateChange", "selectedDate", "autoFocusDay", "size", "inputRef", "menuRef", "setMenuOpen", "dateFormat"]);

  var dateToSet = selectedDate ? dayjs(selectedDate) : dayjs();
  var daysOfTheWeek = useState(dayjs().localeData().weekdaysShort())[0];

  var _d = useState(''),
      currentDay = _d[0],
      setCurrentDay = _d[1];

  var _e = useState([]),
      calendar = _e[0],
      setCalendar = _e[1];

  var _f = useState(dateToSet.format('D')),
      dayTabIndex = _f[0],
      setDayTabIndex = _f[1];

  var _g = useState(parseInt(dayjs(selectedDate).format('D')) || parseInt(dayjs().format('D'))),
      focusedDay = _g[0],
      setFocusedDay = _g[1];

  var _h = useState(false),
      monthChanged = _h[0],
      setMonthChanged = _h[1];

  var dateToRender = selectedDate ? dayjs(selectedDate) : dayjs();

  if (dateToRender.format() === 'Invalid Date') {
    dateToRender = dayjs();
  }

  var _j = useState(dateToRender),
      selectedDateState = _j[0],
      setSelectedDateState = _j[1];

  var prevButtonRef = useRef(null);
  var firstFocusDayButton = useState([])[0];
  var dayButtonRefs = [];
  var theme = useContext(FCTheme);
  var dayNames = daysOfTheWeek.map(function (day) {
    return /*#__PURE__*/React.createElement(DayName, {
      theme: theme.theme,
      key: day
    }, day);
  });

  var getWeeks = function getWeeks() {
    var blankDays = [];
    var daysInMonth = [];
    var blankDaysEnd = [];
    var startOfMonth = selectedDateState.startOf('month');
    var daysInTheMonth = selectedDateState.daysInMonth();
    var endOfMonth = selectedDateState.endOf('month');

    for (var d = 0; d < startOfMonth.day(); d++) {
      blankDays.push({
        day: startOfMonth.subtract(d + 1, 'day').format('D'),
        otherMonth: true,
        date: endOfMonth.subtract(d + 1, 'day').format('YYYY MM DD')
      });
    }

    for (var d = 1; d <= daysInTheMonth; d++) {
      daysInMonth.push({
        day: d,
        date: selectedDateState.format("YYYY-" + (selectedDateState.get('month') + 1) + "-" + d),
        timeStamp: dayjs(selectedDateState.get('year') + "-" + (selectedDateState.get('month') + 1) + "-" + d).format()
      });
    }

    var daysBefore = daysInMonth.length + blankDays.length;
    var numberOfWeeks = dayjs.duration(daysBefore, 'days').asWeeks();
    var totalNumberOfDaysInCalendar = Math.ceil(numberOfWeeks) * 7;

    for (var d = totalNumberOfDaysInCalendar, i = 0; d > daysBefore; d--, i++) {
      blankDaysEnd.push({
        day: endOfMonth.add(i + 1, 'day').format('D'),
        otherMonth: true,
        date: endOfMonth.add(i + 1, 'day').format('YYYY MM DD')
      });
    }

    var totalSlots = __spreadArrays(blankDays.reverse(), daysInMonth, blankDaysEnd);

    var rows = [];
    var cells = [];
    totalSlots.forEach(function (row, i) {
      if (i % 7 !== 0) {
        cells.push(row); // if index not equal 7 that means not go to next week
      } else {
        rows.push(cells); // when reach next week we contain all td in last week to rows

        cells = []; // empty container

        cells.push(row); // in current loop we still push current row to new container
      }

      if (i === totalSlots.length - 1) {
        // when end loop we add remain date
        rows.push(cells);
      }
    });
    setCalendar(rows);

    if (selectedDateState.get('month') === dayjs().get('month')) {
      setCurrentDay(dayjs().format('D'));
    } else {
      setCurrentDay('');
    }
  };

  var calendarKeyPress = function calendarKeyPress(e, timeStamp) {
    if (e.key === 'Enter') {
      onDateChange(dayjs(timeStamp).format(dateFormat));
      if (inputRef) inputRef.current.focus();
    }

    if (e.key === 'ArrowRight') {
      e.preventDefault();

      if (focusedDay + 1 < dayButtonRefs.length) {
        dayButtonRefs[focusedDay + 1].focus();
        setFocusedDay(focusedDay + 1);
      } else {
        setSelectedDateState(selectedDateState.add(1, 'month'));
        setFocusedDay(1);
        setDayTabIndex('1');
        dayButtonRefs[1].focus();
        setMonthChanged(true);
      }
    }

    if (e.key === 'ArrowLeft') {
      e.preventDefault();

      if (focusedDay - 1 > 0) {
        dayButtonRefs[focusedDay - 1].focus();
        setFocusedDay(focusedDay - 1);
      } else {
        var numberOfDaysInMonth = selectedDateState.subtract(1, 'month').daysInMonth();
        setSelectedDateState(selectedDateState.subtract(1, 'month'));
        setDayTabIndex(numberOfDaysInMonth.toString());
        setFocusedDay(numberOfDaysInMonth);
        setMonthChanged(true);
      }
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();

      if (focusedDay + 7 < dayButtonRefs.length) {
        dayButtonRefs[focusedDay + 7].focus();
        setFocusedDay(focusedDay + 7);
      } else {
        setSelectedDateState(selectedDateState.add(1, 'month'));
        setFocusedDay(1);
        setDayTabIndex('1');
        dayButtonRefs[1].focus();
        setMonthChanged(true);
      }
    }

    if (e.key === 'ArrowUp') {
      e.preventDefault();

      if (focusedDay - 7 > 0) {
        dayButtonRefs[focusedDay - 7].focus();
        setFocusedDay(focusedDay - 7);
      } else {
        var numberOfDaysInMonth = selectedDateState.subtract(1, 'month').daysInMonth();
        setSelectedDateState(selectedDateState.subtract(1, 'month'));
        setDayTabIndex(numberOfDaysInMonth.toString());
        setFocusedDay(numberOfDaysInMonth);
        setMonthChanged(true);
      }
    }

    if (e.key === 'Escape') {
      if (inputRef) inputRef.current.focus();
    }
  };

  var calendarRows = calendar.map(function (row) {
    if (row.length > 0) return /*#__PURE__*/React.createElement(Week, {
      key: Math.random()
    }, row.map(function (item, index) {
      return /*#__PURE__*/React.createElement(Day, {
        theme: theme.theme,
        className: "" + (item.day.toString() === currentDay ? "current-day" : '') + (item.otherMonth ? 'other-month' : '') + (item.date && dayjs(item.date).format('MMMM/DD/YYYY') === dayjs(selectedDate).format('MMMM/DD/YYYY') ? ' selected-day' : ''),
        key: item.date
      }, /*#__PURE__*/React.createElement("button", {
        onKeyDown: function onKeyDown(e) {
          return calendarKeyPress(e, item.timeStamp);
        },
        tabIndex: dayTabIndex === item.day.toString() ? 0 : -1,
        ref: function ref(_ref) {
          if (_ref) {
            if (!_ref.disabled) {
              dayButtonRefs[item.day] = _ref;
              if (menuRef) firstFocusDayButton[item.day] = _ref;
            }
          }
        },
        onBlur: function onBlur() {
          var _a;

          if (autoFocusDay) {
            if (dayTabIndex === item.day.toString()) (_a = prevButtonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
          }
        },
        onFocus: function onFocus() {
          return setFocusedDay(parseInt(item.day));
        },
        disabled: item.otherMonth,
        onMouseDown: function onMouseDown() {
          return onDateChange(dayjs(item.timeStamp).format(dateFormat));
        }
      }, /*#__PURE__*/React.createElement("span", {
        "aria-hidden": "false",
        "aria-label": daysOfTheWeek[index] + ' ' + dayjs(item.date).format('MMMM Do YYYY'),
        className: "day-number",
        "aria-selected": !item.otherMonth ? parseInt(item.day) === focusedDay : 'false'
      }, item.day)));
    }));
    return null;
  });

  var handleInputKeyPress = function handleInputKeyPress(e) {
    if (e.key === 'ArrowDown') {
      if (menuRef) {
        if (firstFocusDayButton[focusedDay]) {
          setFocusedDay(focusedDay);
          firstFocusDayButton[focusedDay].focus();
        }
      }
    }

    if (e.key === 'Escape' || e.key === 'Enter') {
      if (menuRef) {
        if (setMenuOpen) {
          onDateChange(dayjs(e.target.value).format() === 'Invalid Date' ? undefined : dayjs(e.target.value).format(dateFormat));
          setMenuOpen(false);
        }
      }
    } else if (e.key !== 'Tab') {
      if (setMenuOpen) setMenuOpen(true);
    }
  };

  useEffect(function () {
    getWeeks();
  }, [selectedDateState]);
  useEffect(function () {
    setSelectedDateState(dateToRender);
  }, [selectedDate]);
  useEffect(function () {
    if (dayButtonRefs) {
      if (monthChanged) {
        if (dayButtonRefs[focusedDay]) dayButtonRefs[focusedDay].focus();
      } else {
        if (firstFocusDayButton[0]) firstFocusDayButton[0].focus();
      }
    }
  }, [dayButtonRefs]);
  useEffect(function () {
    if (dayButtonRefs) {
      if (dayButtonRefs[focusedDay]) dayButtonRefs[focusedDay].focus();
    }
  }, [focusedDay]);
  useEffect(function () {
    if (menuRef) inputRef.current.addEventListener('keydown', function (e) {
      return handleInputKeyPress(e);
    });
    return function () {
      if (menuRef) {
        if (inputRef.current) inputRef.current.removeEventListener('keydown', function (e) {
          return handleInputKeyPress(e);
        });
      }
    };
  }, [menuRef]);

  var nextMonth = function nextMonth() {
    setSelectedDateState(selectedDateState.add(1, 'month'));
    setDayTabIndex('1');
    setFocusedDay(1);
    setMonthChanged(false);
  };

  var previousMonth = function previousMonth() {
    setSelectedDateState(selectedDateState.subtract(1, 'month'));
    setDayTabIndex('1');
    setFocusedDay(1);
    setMonthChanged(false);
  };

  return /*#__PURE__*/React.createElement(CalendarWrapper, __assign({
    calendarWidth: size
  }, rest), /*#__PURE__*/React.createElement(CalendarHeader, null, /*#__PURE__*/React.createElement(CalendarTitle, {
    "aria-live": "assertive",
    theme: theme.theme
  }, /*#__PURE__*/React.createElement("span", null, selectedDateState.format('MMMM') + " " + selectedDateState.format('YYYY'))), /*#__PURE__*/React.createElement(CalendarControl, null, /*#__PURE__*/React.createElement(CalendarControlButtons, {
    title: "Previous month",
    forwardedAs: "a",
    tabIndex: 0,
    onKeyDown: function onKeyDown(e) {
      if (e.key === 'Enter') previousMonth();
    },
    ref: prevButtonRef,
    onClick: function onClick() {
      return previousMonth();
    }
  }, /*#__PURE__*/React.createElement(SvgWrapper, null, /*#__PURE__*/React.createElement(Icon, {
    "aria-label": "Arrow left",
    icon: "chevron-left"
  }))), /*#__PURE__*/React.createElement(CalendarControlButtons, {
    tabIndex: 0,
    title: "Next month",
    forwardedAs: "a",
    onClick: function onClick() {
      return nextMonth();
    },
    onKeyDown: function onKeyDown(e) {
      if (e.key === 'Enter') nextMonth();
    }
  }, /*#__PURE__*/React.createElement(SvgWrapper, null, /*#__PURE__*/React.createElement(Icon, {
    "aria-label": "Arrow right",
    icon: "chevron-right"
  }))))), /*#__PURE__*/React.createElement(Table, {
    role: "group"
  }, /*#__PURE__*/React.createElement("thead", null, /*#__PURE__*/React.createElement("tr", null, dayNames)), /*#__PURE__*/React.createElement("tbody", null, calendarRows)));
};

export default Calendar;
export { Calendar };